import { useState, MouseEventHandler, useEffect, Fragment, useRef } from 'react'
import { kuiperClassNames } from '@opallabs/kuiper'
import { getNestedProperty } from '@opallabs/lambda'
import { colors } from '@opallabs/spectrum'
import AddComponentButton from '../AddComponentButton'
import { getModuleIds } from '../Content/helpers/functions/getModuleIds'
import type {
  GetContentByIDV2Return,
  Module,
  ModuleDefinition,
  OnAddSectionProps,
  OnLinkDataUpdate,
  OnShowAssetPickerProps,
  SectionPostOptions,
} from '../Content/helpers/types'
import { ModuleSection } from './ModuleSection'
import { styles } from './styles'
import { DRAG_STATES } from './types'

export interface ModularComponentBuilderProps {
  addComponentText?: string
  allProps: Record<string, unknown>
  availableModules: ModuleDefinition[]
  displacementHeight?: number
  getContentByIdV2?: (id: number) => Promise<GetContentByIDV2Return>
  getModule?: (
    sectionPostOptions: SectionPostOptions,
    module: Module
  ) => SectionPostOptions
  modules: Module[]
  onAddSection: (args: OnAddSectionProps) => void
  onDeleteSection: (indexToRemove: number) => void
  onExpandAsset: (assetId: number) => void
  onRemoveAsset: (assetId: number, index: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  updateCoverAsset: (
    assetData: FileList,
    assetId: number,
    index: number
  ) => void
  updateLinkData: OnLinkDataUpdate
  updateSectionPostOption: (index: number, value: string, name: string) => void
  uploadAssets: (assets: FileList, args: OnShowAssetPickerProps) => void
  width: number
}

export const ModularComponentBuilder: React.FC<
  ModularComponentBuilderProps
> = ({
  addComponentText = 'Add Module',
  allProps,
  availableModules,
  displacementHeight = 0,
  getContentByIdV2,
  getModule,
  modules,
  onAddSection,
  onDeleteSection,
  onExpandAsset,
  onRemoveAsset,
  onShowAssetPicker,
  updateCoverAsset,
  updateLinkData,
  updateSectionPostOption,
  uploadAssets,
  width,
}) => {
  enum DragElementWidths {
    small = width + 56,
    large = width + 138,
  }

  const [draggedComp, setDraggedComp] = useState<Module | null>(null)
  const [dragState, setDragState] = useState<boolean>(false)
  const [dragZone, setDragZone] = useState<number | null>(null)
  const [quickDrag, setQuickDrag] = useState(false)
  const [tooltipActive, setTooltipActive] = useState(-1)
  const [moduleIds, setModuleIds] = useState(getModuleIds(modules))
  const scrollPosition = useRef(0)
  const timeouts: (NodeJS.Timeout | number)[] = []
  const modularComponentBuilderRef = useRef<HTMLDivElement>(null)
  const emptyModules = !modules || (modules && modules.length === 0)

  window.addEventListener('scroll', (e) => handleScroll(e), true)

  const handleScroll = (e: Event) => {
    const path = (e as unknown as { path: Element[] }).path
    if (path && path.length > 0) {
      scrollPosition.current = path[0].scrollTop
    }
  }

  useEffect(() => {
    // clean up code
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  })

  useEffect(() => {
    // make sure that a user isn't trying to drag and drop too quickly
    if (!draggedComp) {
      setTimeout(() => {
        const dragElements = document.querySelectorAll(
          "[id^='kuiper-dnd-grabber']"
        )
        dragElements.forEach((dragElement) => {
          dragElement?.remove()
        })
      }, 91)
    }
  }, [draggedComp])

  const updateComponentPositions = (zone: number) => {
    let desiredIndex = zone
    if (!draggedComp) return
    const currentIndex = draggedComp?.position
    if (desiredIndex === currentIndex + 1 || desiredIndex === currentIndex)
      return
    if (currentIndex < desiredIndex || desiredIndex === modules?.length)
      desiredIndex -= 1
    updateSectionPostOption(
      currentIndex,
      desiredIndex.toString(),
      'section position'
    )
    setModuleIds(getModuleIds(modules))
  }

  const handleSaveSectionOption = (
    index: number,
    value: string,
    name: string
  ) => {
    updateSectionPostOption(index, value, name)
    // on a section reorder, assign new keys and force a re-render
    if (name === 'section position') setModuleIds(getModuleIds(modules))
  }

  const handleDeleteSection = (index: number) => {
    onDeleteSection(index)
    // on a section reorder, assign new keys and force a re-render
    if (index !== modules.length - 1) setModuleIds(getModuleIds(modules))
  }

  const onMouseMove = (document: unknown, component: Module, e: MouseEvent) => {
    moveAt(e.clientY, component)
  }

  const moveAt = (clientY: number, component: Module) => {
    const componentIndex: number | undefined =
      draggedComp?.position || component?.position
    const currentDragEl = document.querySelector(
      `#kuiper-dnd-grabber-${componentIndex}`
    ) as HTMLElement
    if (!currentDragEl) return
    const dragElPosition =
      clientY -
      currentDragEl.offsetHeight -
      168 -
      displacementHeight +
      scrollPosition.current
    currentDragEl.style.top = `${dragElPosition}px`
  }

  const onDrag = (e: MouseEvent, component: Module) => {
    setQuickDrag(true)
    timeouts[0] = setTimeout(() => {
      setQuickDrag(false)
    }, 110)
    const dragElements = document.querySelectorAll("[id^='kuiper-dnd-grabber']")
    dragElements.forEach((dragElement) => {
      dragElement?.remove()
    })

    let originElement: HTMLElement | null = e.target as HTMLElement
    while (
      originElement?.classList &&
      originElement.classList[0].includes('kuiper-icon')
    ) {
      originElement = originElement.parentElement
    }
    if (!originElement) return

    setDraggedComp(component)
    setDragZone(component.position || -1)
    setDragState(true)

    const hasAssetButtons = Object.keys(component).find((key) =>
      key.toLowerCase().includes('asset')
    )
    const dragElementWidth = hasAssetButtons
      ? `${DragElementWidths.large}px`
      : `${DragElementWidths.small}px`

    const newDragElement = originElement.cloneNode(true) as HTMLElement
    newDragElement.setAttribute(
      'style',
      `position: relative; width: ${dragElementWidth}; z-index: 1004; cursor: grabbing;`
    )
    newDragElement.innerText = DRAG_STATES.DRAG
    newDragElement.id = `kuiper-dnd-grabber-${component.position}`
    const modularCompBuilders = document.querySelectorAll(
      '.kuiper-modular-component-builder'
    )
    modularCompBuilders[modularCompBuilders.length - 1].prepend(newDragElement)
    moveAt(e.clientY, component)

    document.addEventListener(
      'mousemove',
      onMouseMove.bind(document, e, component)
    )
  }

  const onDragEnter: MouseEventHandler = (e) => {
    const dragZoneElement = e.target
    if (!dragZoneElement) return
    const currentDragZone = parseInt(
      getNestedProperty(dragZoneElement, 'dataset', 'zoneId') || '',
      10
    )

    setDragZone(currentDragZone)

    const componentIndex: number = draggedComp ? draggedComp.position : -1
    const dragElement: HTMLElement | null = document.querySelector(
      `#kuiper-dnd-grabber-${componentIndex}`
    )
    let attachedElement: HTMLElement | null = null

    if (currentDragZone === modules?.length) {
      const attachedElements = document.querySelectorAll(
        `.kuiper-module-section-${currentDragZone - 1}`
      )
      attachedElement = attachedElements[
        attachedElements.length - 1
      ] as HTMLElement
    } else {
      const attachedElements = document.querySelectorAll(
        `.kuiper-module-section-${currentDragZone}`
      )
      attachedElement = attachedElements[
        attachedElements.length - 1
      ] as HTMLElement
    }

    if (dragElement) {
      dragElement.innerText = DRAG_STATES.PLACE
      dragElement.style.width = `${width}px`
      dragElement.style.backgroundColor = colors.gem.blue
      dragElement.style.color = 'white'
      dragElement.style.borderRadius = '0px'
      let insertElementMode: InsertPosition = 'beforebegin'
      if (currentDragZone === modules?.length) {
        insertElementMode = 'afterend'
      }
      if (!quickDrag) {
        timeouts[1] = setTimeout(() => {
          dragElement.style.position = 'static'
          dragElement.style.top = '0px'
          attachedElement?.insertAdjacentElement(insertElementMode, dragElement)
        }, 90)
      }
    }
  }

  const onDragLeave: MouseEventHandler = (e) => {
    const dragDirection = e.nativeEvent.offsetY >= 0 ? 0 : -1
    let hoveredModuleIndex = +(dragZone || 0) + dragDirection
    hoveredModuleIndex = hoveredModuleIndex < 0 ? 0 : hoveredModuleIndex
    hoveredModuleIndex =
      hoveredModuleIndex >= modules?.length
        ? modules?.length - 1
        : hoveredModuleIndex
    const hoveredModule = modules[hoveredModuleIndex]
    const hasAssetButtons = Object.keys(hoveredModule).find((key) =>
      key.toLowerCase().includes('asset')
    )
    const dragElementWidth = hasAssetButtons
      ? `${DragElementWidths.large}px`
      : `${DragElementWidths.small}px`

    const componentIndex: number = draggedComp ? draggedComp.position : -1
    const dragElement: HTMLElement | null = document.querySelector(
      `#kuiper-dnd-grabber-${componentIndex}`
    )
    const modularCompBuilders = document.querySelectorAll(
      '.kuiper-modular-component-builder'
    )
    if (dragElement) {
      dragElement.innerText = DRAG_STATES.DRAG
      dragElement.style.width = dragElementWidth
      dragElement.style.backgroundColor = colors.n3.base
      dragElement.style.borderRadius = '0px 3px 3px 0'
      dragElement.style.color = colors.n5.base
      dragElement.style.position = 'relative'
      modularCompBuilders[modularCompBuilders.length - 1].prepend(dragElement)
    }

    setDragZone(null)
  }

  const onDragComplete: MouseEventHandler = (e) => {
    clearTimeout(timeouts[0] as NodeJS.Timeout)
    clearTimeout(timeouts[1] as NodeJS.Timeout)
    const dragZoneElement = e.target
    if (!dragZoneElement) return
    if (dragZone !== null) updateComponentPositions(dragZone)

    setDragZone(null)
    setDragState(false)
    setDraggedComp(null)

    const dragElements = document.querySelectorAll("[id^='kuiper-dnd-grabber']")
    dragElements.forEach((dragElement) => {
      dragElement?.remove()
    })

    document.removeEventListener('mousemove', onMouseMove as EventListener)
  }

  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <Fragment>
      <div
        ref={modularComponentBuilderRef}
        className={kuiperClassNames('modular-component-builder')}
        css={styles.getContainerStyles(width, emptyModules, dragState)}
        onMouseUp={dragState ? onDragComplete : (): null => null}
      >
        {emptyModules ? (
          <div css={styles.emptyContainerStyles}>
            <div css={styles.emptyTextStyles}>Nothing added yet</div>
          </div>
        ) : (
          modules?.map((mod, index) => (
            <div
              key={`mod-${
                (mod.componentName || mod.name) as string
              }-sect-${index}-${moduleIds ? moduleIds[index] : 0}`}
              className={kuiperClassNames(`module-section-${index}`)}
            >
              <ModuleSection
                key={`mod-${
                  (mod.componentName || mod.name) as string
                }-sect-${index}-${moduleIds ? moduleIds[index] : 0}`}
                allProps={allProps}
                availableModules={availableModules}
                draggedComp={draggedComp}
                dragZone={dragZone}
                getContentByIdV2={getContentByIdV2}
                getModule={getModule}
                index={index}
                isDragging={dragState}
                isPresenting={allProps.isPresenting as boolean}
                module={modules[index]}
                modules={modules || []}
                onDeleteSection={(index: number) => handleDeleteSection(index)}
                onDrag={onDrag}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onExpandAsset={onExpandAsset}
                onRemoveAsset={onRemoveAsset}
                onShowAssetPicker={onShowAssetPicker}
                onTooltipToggle={(index: number) => setTooltipActive(index)}
                readOnly={allProps.readOnly as boolean}
                tooltipActive={tooltipActive === index}
                updateCoverAsset={updateCoverAsset}
                updateLinkData={updateLinkData}
                updateSectionOptions={handleSaveSectionOption}
                uploadAssets={uploadAssets}
                width={width}
              />
            </div>
          ))
        )}
      </div>
      {!allProps.readOnly && !allProps.isPresenting && (
        <div css={styles.buttonContainerStyle}>
          <AddComponentButton
            ariaLabel="Add New Post Section"
            label={addComponentText}
            options={availableModules}
            onSelect={(selected: ModuleDefinition): void =>
              onAddSection({ componentName: selected.componentName })
            }
            hasMenu={true}
          />
        </div>
      )}
    </Fragment>
  )
}
